import { CSSProperties } from "react";

import { colors } from "@dotnet/telematics-design-system";

import { CELL_ROLE, StripParams } from "../../shared/types";

const BUBBLE_WIDTH = 76;

const getBaseStyle = (
  fill: string,
  visible: boolean,
  isPreview: boolean
): CSSProperties => ({
  position: "absolute",
  top: 0,
  bottom: 0,
  background: fill,
  zIndex: 0,
  pointerEvents: "none",
  opacity: visible ? 1 : 0,
  transition: isPreview
    ? "opacity 0ms 0ms"
    : "opacity 220ms cubic-bezier(0.4, 0, 0.2, 1)"
});

const getStripPositionStyle = (role: CELL_ROLE): CSSProperties => {
  switch (role) {
    case CELL_ROLE.Start:
      return { left: BUBBLE_WIDTH / 2, right: 0, borderRadius: 0 };
    case CELL_ROLE.End:
      return { left: 0, right: BUBBLE_WIDTH / 2, borderRadius: 0 };
    case CELL_ROLE.Middle:
      return { left: 0, right: 0, borderRadius: 0 };
    default:
      return {};
  }
};

const getCellRole = (
  monthKey: number,
  rangeStart: number,
  rangeEnd: number
) => {
  const start = Math.min(rangeStart, rangeEnd);
  const end = Math.max(rangeStart, rangeEnd);

  if (monthKey === start) return CELL_ROLE.Start;
  if (monthKey === end) return CELL_ROLE.End;
  if (monthKey > start && monthKey < end) return CELL_ROLE.Middle;

  return CELL_ROLE.None;
};

export const useStripStyle = () => {
  const stripStyle = (monthKey: number, params: StripParams): CSSProperties => {
    const { rangeStart, rangeEnd, isPreview } = params;

    const startOrEndNull = rangeStart === null || rangeEnd === null;

    if (startOrEndNull || rangeStart === rangeEnd) {
      return getBaseStyle(colors.blue700, false, isPreview);
    }

    const role = getCellRole(monthKey, rangeStart, rangeEnd);

    if (role === CELL_ROLE.None) {
      return getBaseStyle(colors.blue700, false, isPreview);
    }

    const fill = isPreview ? colors.gray100 : colors.blue700;

    return {
      ...getBaseStyle(fill, true, isPreview),
      ...getStripPositionStyle(role)
    };
  };

  return { stripStyle };
};
